import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import { HarborKYCData } from '@/types/harbor-management.types';
import { UserKYCData } from '@/types/user-kyc.types';

export interface KYCListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  entityType?: 'harbor' | 'club' | 'service_provider';
  kycStatus?:
    | 'pending'
    | 'under_review'
    | 'approved'
    | 'rejected'
    | 'requires_update'
    | 'not_required'
    | 'on_hold'
    | 'resubmitted';
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export const KYCService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getKYCList: builder.query<
      PaginatedApiResponse<HarborKYCData[]>,
      KYCListQueryParams
    >({
      query: (params) => ({
        url: 'admin/kyc',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          entityType: params.entityType,
          kycStatus: params.kycStatus,
          sort_by: params.sort_by,
          sort_order: params.sort_order,
        },
      }),
      transformResponse: (response: PaginatedApiResponse<HarborKYCData[]>) =>
        response,
      providesTags: ['KYC'],
      keepUnusedDataFor: 0,
    }),

    getKYCById: builder.query<ApiResponse<HarborKYCData>, string>({
      query: (id) => ({
        url: `admin/kyc/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      providesTags: (result, error, id) => [{ type: 'KYC', id }],
      keepUnusedDataFor: 0,
    }),

    updateKYCStatus: builder.mutation<
      ApiResponse<HarborKYCData>,
      { kycId: string; data: { kyc_status: string; admin_notes: string } }
    >({
      query: ({ kycId, data }) => ({
        url: `admin/kyc/${kycId}/status`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      invalidatesTags: (result, error, { kycId }) => [
        { type: 'KYC', id: kycId },
        { type: 'KYC', id: 'LIST' },
      ],
    }),

    getPendingEntities: builder.query<
      Array<{
        entity_type: string;
        entity_id: string;
        name: string;
      }>,
      'harbor' | 'club' | 'service_provider'
    >({
      query: (entityType) => ({
        url: `kyc/pending/entities`,
        method: 'GET',
        params: {
          entityType,
        },
      }),
      keepUnusedDataFor: 0,
      transformResponse: (
        response: Array<{
          entity_type: string;
          entity_id: string;
          name: string;
        }>
      ) => response,
    }),

    getUserKYC: builder.query<
      UserKYCData[] | PaginatedApiResponse<UserKYCData[]>,
      { page?: number; limit?: number; search?: string } | void
    >({
      // Pass page, limit, and search as query parameters
      query: (params) => ({
        url: 'kyc/user',
        method: 'GET',
        params: params
          ? {
              ...(params.page !== undefined && { page: params.page }),
              ...(params.limit !== undefined && { limit: params.limit }),
              ...(params.search !== undefined &&
                params.search !== '' && { search: params.search }),
            }
          : undefined,
      }),
      transformResponse: (
        response: UserKYCData[] | PaginatedApiResponse<UserKYCData[]>
      ) => response,
      providesTags: ['KYC'],
      keepUnusedDataFor: 0,
    }),

    getUserKYCById: builder.query<UserKYCData, string>({
      query: (id) => ({
        url: `kyc/user/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: UserKYCData) => response,
      providesTags: (result, error, id) => [{ type: 'KYC', id }],
      keepUnusedDataFor: 0,
    }),

    createKYC: builder.mutation<ApiResponse<UserKYCData>, Partial<UserKYCData>>(
      {
        query: (data) => ({
          url: 'kyc',
          method: 'POST',
          data,
        }),
        transformResponse: (response: ApiResponse<UserKYCData>) => response,
        invalidatesTags: ['KYC'],
      }
    ),

    updateKYC: builder.mutation<
      ApiResponse<UserKYCData>,
      { id: string; data: Partial<UserKYCData> }
    >({
      query: ({ id, data }) => ({
        url: `kyc/${id}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: ApiResponse<UserKYCData>) => response,
      invalidatesTags: (result, error, { id }) => [{ type: 'KYC', id }, 'KYC'],
    }),
  }),
});

export const {
  useGetKYCListQuery,
  useGetKYCByIdQuery,
  useUpdateKYCStatusMutation,
  useGetUserKYCQuery,
  useGetUserKYCByIdQuery,
  useCreateKYCMutation,
  useUpdateKYCMutation,
  useGetPendingEntitiesQuery,
} = KYCService;
